/**
 * historicalLineageTool
 *
 * A realtime function-tool the Qoryn agent invokes ON DEMAND when the
 * Founder asks an explicit historical/lineage question such as:
 *   - "Did we ever consider X?"
 *   - "What did we use before?"
 *   - "What was the previous decision?"
 *   - "Why did we change from X to Y?"
 *   - "What did we originally decide?"
 *   - "How did this evolve?"
 *
 * NOT injected at session bootstrap. Bootstrap remains byte-identical to
 * Phase 1D — only the current head of every supersession chain is in the
 * injected CURRENT PROJECT CONTEXT block. This tool is what lets Qoryn
 * accurately narrate historical trails without violating that invariant.
 *
 * Non-negotiable invariants (enforced server-side, mirrored here):
 *   - Owner-scoped (JWT + explicit owner_id predicate).
 *   - Deterministic keyword matching (NO embeddings, NO semantic sim).
 *   - Multi-hop chain walking with cycle guard.
 *   - Provenance-preserving output — the model receives the raw fields
 *     but is instructed by the base prompt to synthesize a natural
 *     conversational reply and never recite metadata.
 */

import { tool } from "@openai/agents-realtime";
import { fetchSupersessionLineage } from "@/lib/api";

/**
 * Build the tool bound to a specific project scope. Called once per
 * connect() from useRealtimeSession, so the closure captures the
 * current active project id at connect time. Subsequent tool
 * invocations reuse the same JWT via getAccessToken() inside the API
 * helper, so token refreshes are honored transparently.
 *
 * @param {object} opts
 * @param {string|null} opts.projectId - active project UUID, or null to
 *   search all owner-visible projects.
 * @returns {import("@openai/agents-realtime").FunctionTool}
 */
export function buildHistoricalLineageTool({ projectId } = {}) {
    return tool({
        name: "get_supersession_lineage",
        description: [
            "Fetch the historical supersession trail for a topic when the",
            "Founder asks an explicit historical or lineage question.",
            "",
            "Use ONLY for questions like: 'did we ever consider X', 'what",
            "did we use before', 'what was the previous decision', 'why",
            "did we change from X to Y', 'how did this evolve', 'what did",
            "we originally decide'.",
            "",
            "Do NOT use for questions about the current authoritative",
            "value (those are already answered by the CURRENT PROJECT",
            "CONTEXT block injected at session start).",
            "",
            "Returns the multi-generation chain: the current head plus",
            "every superseded ancestor, with source session/turn,",
            "source_type, approval_state, and superseded_by /",
            "superseded_at markers. When no lineage matches, chains is",
            "empty — say so honestly, never fabricate history.",
        ].join(" "),
        parameters: {
            type: "object",
            properties: {
                query: {
                    type: "string",
                    description: [
                        "Short topic or entity phrase to search for",
                        "(e.g. 'five agents', 'WidgetLib', 'authentication",
                        "provider'). 1-200 chars. Deterministic substring",
                        "match — pass concrete keywords, not questions.",
                    ].join(" "),
                    minLength: 1,
                    maxLength: 200,
                },
            },
            required: ["query"],
            additionalProperties: false,
        },
        strict: true,
        execute: async ({ query }) => {
            try {
                const payload = await fetchSupersessionLineage({
                    query,
                    project_id: projectId,
                });
                // Return the payload as-is; the SDK JSON-stringifies it
                // for the model. The base prompt is responsible for
                // teaching Qoryn to synthesize this into a natural
                // reply and never recite raw metadata.
                return JSON.stringify(payload);
            } catch (err) {
                // Never throw across the tool boundary — the SDK would
                // surface an opaque error to the model. Return a
                // structured honest failure so Qoryn can say "I can't
                // pull that up right now."
                return JSON.stringify({
                    error: true,
                    reason:
                        err && err.status
                            ? `history endpoint returned ${err.status}`
                            : "history endpoint unreachable",
                    query,
                });
            }
        },
    });
}

export default buildHistoricalLineageTool;
